// The "Check your design" dialog and the Confirm flow.
//   Confirm -> build the files -> POST /api/designs -> PUT each file -> POST .../complete -> go back to the shop.
// Nothing is uploaded before the customer confirms. If a step fails, the design on screen is untouched
// and "Try again" carries on where it stopped.
// Gifts ticked in the strip of this dialog (more-gifts.js) are saved the same way, each as its own design after the main one:
// only its print files (SVG and preview per side) are sent, not the photo files again. The link back to the shop
// carries the main design (rg_design) and the gifts (rg_extra).
// In the layer over the store's product page (ctx.embed, see embed.js) there is no redirect: the page sends the same values in an
// rg:done message and the store page puts the design in the cart.

import { $, esc, formatBytes, shortTitle, STARTER_TEXT } from "./util.js";
import { buildFiles, previewPNG } from "./svg-export.js";
import * as api from "./api.js";
import { discountText, totalOf } from "./bulk.js";
import { isBackSide } from "./products.js";
import { MAX_FILES, MAX_PHOTO_BYTES, MAX_SVG_BYTES, MAX_PREVIEW_BYTES, MAX_NAMES_BYTES, MAX_TOTAL_BYTES, ALLOWED_PHOTO_TYPES } from "./limits.js";

// A problem the customer can fix by changing the design. No point trying again as it is.
class DesignTooBig extends Error {}

// ctx: { shop, product (template id), handle, variant, returnUrl ("" in preview mode), embed (the layer over the store page, or null) }
// gifts: the gifts strip (more-gifts.js), or null. view3d: "See it in 3D" and the customer's suitcase size (view3d.js), or null.
export function initConfirm({ editor, ctx, gifts = null, view3d = null }) {
  const previewMode = !ctx.returnUrl && !ctx.embed;
  const review = $("review"), scrim = $("scrim"), chk = $("chkOk"), btn = $("btnConfirm"), closeBtn = $("btnCloseReview");
  const box = $("uploadBox"), done = $("doneBox");
  let lastFocus = null, openToken = 0, printUrl = "", moreUrls = [];
  let submitting = false, finished = false;
  let jobs = [];           // the designs to save: the main one, then each gift. Each keeps its own work done so far (built files, design id, files already sent)
  let extras = [];         // the gifts ticked in the strip (they are read again when the customer confirms)
  let blocked = false;     // the design cannot be sent as it is (empty, or too many files)

  const isOpen = () => !review.hidden;

  // ---------- open and close ----------
  async function open() {
    lastFocus = document.activeElement;
    const token = ++openToken;
    extras = gifts ? gifts.selected() : []; jobs = makeJobs(); finished = false; blocked = false;
    if (gifts) gifts.load();                    // the gifts strip fills in on its own; the order below follows its ticks
    if (view3d) view3d.sync();                  // "See it in 3D" (luggage covers) and the size the customer set
    // The tick box waits until the pictures and the warnings are ready (a double-sided design takes a moment longer),
    // so nobody ticks it before the page knows whether the design can be sent.
    box.hidden = true; done.hidden = true; chk.checked = false; chk.disabled = true; btn.disabled = true; closeBtn.disabled = false;
    scrim.hidden = false; review.hidden = false; closeBtn.focus();
    $("revWarn").innerHTML = ""; $("revNames").innerHTML = ""; $("revSides").innerHTML = ""; $("revMore").innerHTML = "";

    // A product with more than one side (front and back, or a pack with an item on each side) shows each side that has a design,
    // and says which sides keep the original.
    const sideList = editor.sides(), two = sideList.length > 1;
    const designed = sideList.filter((s) => s.hasDesign);
    const shown = designed.length ? designed : [sideList[editor.sideIndex()]];
    const capOf = (s, text) => (two ? `${s.label} · ${text}` : text[0].toUpperCase() + text.slice(1));
    $("capMock").textContent = capOf(shown[0], "on the cover");
    $("capPrint").textContent = capOf(shown[0], "what we print (preview size)");
    if (two) $("revSides").innerHTML = sideList.map((s) => `<li><b>${esc(s.label)}:</b> ${s.hasDesign ? "your design" : "original design, not changed"}</li>`).join("");

    // the pictures: a mockup and a print preview for each side shown
    try {
      const cover = editor.currentCover(), pics = [];
      for (const s of shown) {
        const mock = await editor.withSide(s.index, () => editor.renderMock());
        pics.push({ s, mock, blob: await previewPNG(editor, cover, s.index) });
      }
      if (token !== openToken) return;
      freeUrls();
      $("revMock").src = pics[0].mock;
      printUrl = URL.createObjectURL(pics[0].blob);
      $("revPrint").src = printUrl;
      for (const p of pics.slice(1)) $("revMore").append(morePictures(p, capOf));
    } catch (e) { /* the pictures are a nice-to-have; the files are built again on Confirm */ }
    if (token !== openToken) return;

    renderWarnings();
    showOrder();
    chk.onchange = () => { btn.disabled = blocked || submitting || !chk.checked; };
    chk.disabled = false;
  }
  // The warnings under the pictures, and whether the design can be sent (blocked). Run again when the suitcase size changes.
  function renderWarnings() {
    blocked = false;
    const sideList = editor.sides(), two = sideList.length > 1, designed = sideList.filter((s) => s.hasDesign);
    const warns = [], bad = (text) => { blocked = true; warns.push(`<span class="chip q-bad"><span class="dot"></span>${esc(text)}</span>`); };
    const plan = editor.coverPlan(), photos = editor.photos().filter((p) => p.original), bulk = editor.bulk;
    if (editor.isEmpty()) bad("Your cover is empty. Close this and add a photo or some text.");
    if (editor.starterLeft()) bad(`Your design still says “${STARTER_TEXT}”. Close this and type your own words in the box under the design, or remove it.`);
    bulk.problems().forEach(bad);                              // no names, a name without a size, no copies ...
    if (plan.master && !editor.nameObj()) bad("The design has no name on it. Close this, tap a text, and tap “Use the selected text for names” in the Names tab.");
    const perCover = 2 * Math.max(1, designed.length);       // an SVG and a preview for each side that has a design
    const fileCount = photos.length + perCover + (plan.master ? 1 : 0);
    if (fileCount > MAX_FILES) bad(`That is too many photos in one order. Use up to ${Math.max(1, MAX_FILES - perCover - (plan.master ? 1 : 0))} photos.`);
    for (const s of sideList) {                               // photos that may not print sharp, side by side
      const weak = editor.sideObjects(s.index).map(editor.quality).filter((q) => q && q.cls !== "q-ok");
      if (weak.length) warns.push(`<span class="chip ${weak.some((q) => q.cls === "q-bad") ? "q-bad" : "q-warn"}"><span class="dot"></span>${two ? esc(s.label) + ": " : ""}${weak.length} photo${weak.length > 1 ? "s" : ""} may not print sharp. Make ${weak.length > 1 ? "them" : "it"} smaller to fix.</span>`);
    }
    // A pack or a pair: an item with no design keeps its original design. Say which ones, so the customer notices (this does not stop them).
    const items = sideList.filter((s) => !isBackSide(s.id)), unmade = items.filter((s) => !s.hasDesign);
    if (items.length > 1 && unmade.length && unmade.length < sideList.length) {
      const names = unmade.map((s) => s.label).join(", "), many = unmade.length > 1;
      warns.push(`<span class="chip q-warn"><span class="dot"></span>${unmade.length} of ${items.length} items ${many ? "have" : "has"} no design: ${esc(names)}. ${many ? "They keep" : "It keeps"} the original design.</span>`);
    }
    if (view3d && view3d.problem()) bad(view3d.problem());
    $("revWarn").innerHTML = warns.join(" ");
  }
  // What the order is (under the pictures). Shown again when the gifts strip changes.
  function showOrder() {
    if (!submitting && !finished && gifts) extras = gifts.selected();
    $("revNames").innerHTML = (extras.length ? giftsSummary() : orderSummary(editor.bulk)) + (previewMode ? `<p class="note">Preview mode: this page was opened on its own, so nothing goes into a cart.</p>` : "");
  }
  // What the order is: one cover, or a bulk order with its total, sizes, discount and (for names) the names.
  const SHOWN_NAMES = 12;
  function orderSummary(bulk) {
    const mode = bulk.mode();
    if (mode === "single") return `<p><b style="color:var(--ink)">1 cover</b></p>${sizeLine()}`;
    const n = bulk.count(), items = `${n} item${n === 1 ? "" : "s"}`, sizes = bulk.sizeWords();
    const d = discountText(n), rows = bulk.rows();
    return `<p><b style="color:var(--ink)" class="num">${items}</b>${mode === "names" ? ", each with its own name." : ", all the same design."}</p>`
      + (sizes ? `<p>Sizes: ${esc(sizes)}</p>` : "")
      + (mode === "names" ? `<div class="names-out">${rows.slice(0, SHOWN_NAMES).map((r) => `<span>${esc(r.name)}</span>`).join("")}${rows.length > SHOWN_NAMES ? `<span>and ${rows.length - SHOWN_NAMES} more</span>` : ""}</div>
        <p>You send the design once. Our team makes one print file for each name.</p>` : "")
      + `<div class="discount"><b>${esc(d.line)}</b><span>${esc(d.nudge)}</span></div>`;
  }
  // The customer's own suitcase size (luggage covers), under the order.
  const sizeLine = () => { const t = view3d ? view3d.sizeText() : ""; return t ? `<p class="size-line">Your suitcase: ${esc(t)}</p>` : ""; };
  // The order when gifts were added: the main item and each gift with its size, and the discount for the number of items.
  function giftsSummary() {
    const d = discountText(1 + extras.length);
    return `<p><b style="color:var(--ink)" class="num">${1 + extras.length} items</b>: your design and ${extras.length} gift${extras.length === 1 ? "" : "s"}.</p>
      <ul class="order-list"><li><span class="ol-name">${esc(shortTitle(editor.product.title))}</span><span class="ol-note">Your design${view3d && view3d.sizeText() ? ` · ${esc(view3d.sizeText())}` : ""}</span></li>${extras.map((g) => `<li>
        <img src="${esc(g.preview)}" alt=""><span class="ol-name">${esc(shortTitle(g.title))}</span><span class="ol-note">${g.sizeLabel ? esc(g.sizeLabel) : "Gift"}</span></li>`).join("")}</ul>
      <div class="discount"><b>${esc(d.line)}</b><span>${esc(d.nudge)}</span></div>`;
  }
  // The pictures of one more side (the back, when the front is shown first).
  function morePictures({ s, mock, blob }, capOf) {
    const box = document.createElement("div"); box.className = "review-imgs";
    box.innerHTML = `<figure><img alt="Your ${esc(s.label.toLowerCase())} design on the cover"><figcaption>${esc(capOf(s, "on the cover"))}</figcaption></figure>
      <figure><img class="checker" alt="The ${esc(s.label.toLowerCase())} print area only"><figcaption>${esc(capOf(s, "what we print (preview size)"))}</figcaption></figure>`;
    const [a, b] = box.querySelectorAll("img");
    a.src = mock;
    const url = URL.createObjectURL(blob); moreUrls.push(url); b.src = url;
    return box;
  }
  function freeUrls() {
    if (printUrl) { URL.revokeObjectURL(printUrl); printUrl = ""; }
    moreUrls.forEach((u) => URL.revokeObjectURL(u)); moreUrls = [];
  }
  function close() {
    if (submitting) return;                      // do not walk away in the middle of an upload
    openToken++; review.hidden = true; scrim.hidden = true; freeUrls();
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  closeBtn.addEventListener("click", close);
  scrim.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (!isOpen() || document.querySelector(".adjust-layer") || (view3d && view3d.isOpen())) return;     // the Adjust editor of a gift, or the 3D sheet, is on top: its keys are its own
    if (e.key === "Escape") { close(); return; }
    if (e.key !== "Tab") return;                 // keep keyboard focus inside the dialog
    const items = [...review.querySelectorAll("button, input, select, a[href]")].filter((el) => !el.disabled && !el.hidden && el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (!review.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  // coming back with the browser's Back button must not show a finished dialog
  addEventListener("pageshow", (e) => { if (e.persisted && finished) location.reload(); });

  // ---------- messages inside the dialog ----------
  function showProgress(text, doneCount, total) {
    done.hidden = true; box.hidden = false; box.className = "upload-box";
    box.innerHTML = `<p>${esc(text)}</p><progress aria-label="${esc(text)}" ${total ? `max="${total}" value="${doneCount}"` : ""}></progress>`;
  }
  function showError(e) {
    done.hidden = true; box.hidden = false; box.className = "upload-box err";
    const tooBig = e instanceof DesignTooBig;
    const msg = e instanceof api.ApiError || tooBig ? e.message : "Something went wrong while saving your design.";
    box.innerHTML = `<h3>${tooBig ? "Your design is too big to send" : "Your design was not saved yet"}</h3>
      <p>${esc(msg)}</p>
      ${tooBig ? "" : "<p>Your design is still here. Nothing was lost.</p>"}
      <div class="group"><button class="btn primary" type="button" id="btnRetry">${tooBig ? "Keep editing" : "Try again"}</button></div>`;
    const retry = $("btnRetry");
    retry.addEventListener("click", tooBig ? close : confirm);
    retry.focus();
  }
  function showDone(staffHref) {
    box.hidden = true; done.hidden = false;
    done.innerHTML = `<h3>Design confirmed (preview mode)</h3>
      <p>Your design is saved. Nothing was added to a cart, because this page was opened on its own.</p>
      <div class="group"><a class="btn primary" id="lnkStaff" href="${esc(staffHref)}">Open the staff page</a><button class="btn" type="button" id="btnKeepEditing">Keep editing</button></div>`;
    $("btnKeepEditing").addEventListener("click", () => { editor.resumeSaving(); close(); });
    $("lnkStaff").focus();
  }

  // ---------- the file list for the upload service ----------
  const fileId = (kind, cover, side) => `${kind}-${cover.index}-${side.id}`;      // svg-1-front, preview-1-back
  // withPhotos false (a gift): the photos are not sent again. The SVG of the gift already has them inside.
  function plan(built, withPhotos = true) {
    const files = [];
    if (withPhotos) built.photos.forEach((p, i) => files.push({ id: `photo-${i + 1}`, kind: "photo", filename: p.filename, contentType: p.file.type, blob: p.file }));
    built.covers.forEach((c) => c.sides.forEach((sd) => {
      if (sd.keepOriginal) return;                       // a side left empty has no files
      files.push({ id: fileId("svg", c, sd), kind: "svg", filename: sd.svgFilename, contentType: "image/svg+xml", blob: sd.svgBlob });
      files.push({ id: fileId("preview", c, sd), kind: "preview", filename: sd.previewFilename, contentType: "image/png", blob: sd.previewBlob });
    }));
    if (built.namesBlob) files.push({ id: "names", kind: "names", filename: "names.json", contentType: "application/json", blob: built.namesBlob });   // the names of a bulk order
    return files;
  }
  function checkLimits(files) {
    const tooBig = (m) => { throw new DesignTooBig(m); };
    if (!files.some((f) => f.kind === "svg")) tooBig("Your design is empty. Close this and add a photo or some text.");
    if (files.length > MAX_FILES) tooBig("There are too many files in this order. Use fewer names or fewer photos.");
    let total = 0;
    for (const f of files) {
      total += f.blob.size;
      if (f.kind === "photo" && !ALLOWED_PHOTO_TYPES.includes(f.contentType)) tooBig(`“${f.filename}” is not a JPG, PNG or WebP photo. Remove it and add it again.`);
      if (f.kind === "photo" && f.blob.size > MAX_PHOTO_BYTES) tooBig(`“${f.filename}” is bigger than 25 MB. Remove it and choose a smaller photo.`);
      if (f.kind === "svg" && f.blob.size > MAX_SVG_BYTES) tooBig("The print file is too big (over 60 MB). Use fewer or smaller photos.");
      if (f.kind === "preview" && f.blob.size > MAX_PREVIEW_BYTES) tooBig("The preview picture is too big. Try a simpler design.");
      if (f.kind === "names" && f.blob.size > MAX_NAMES_BYTES) tooBig("The list of names is too long to send in one order. Split it into two orders.");
    }
    if (total > MAX_TOTAL_BYTES) tooBig(`Everything together is ${formatBytes(total)}. The limit is 200 MB. Use fewer photos or fewer names.`);
  }
  // job.ctx: { shop, product, handle, variant } of this design. job.isMain: the main design (it can be a bulk order).
  function payload(job, built, files) {
    const c0 = job.ctx;
    const out = {
      shop: c0.shop || "",
      product: c0.product,
      covers: built.covers.map((c) => {
        const sides = c.sides.map((sd) => (sd.keepOriginal
          ? { id: sd.id, label: sd.label, keepOriginal: true }
          : { id: sd.id, label: sd.label, svgFileId: fileId("svg", c, sd), previewFileId: fileId("preview", c, sd) }));
        const first = sides.find((sd) => !sd.keepOriginal);      // the old fields point at the first side with files, so old readers still work
        const o = { index: c.index, svgFileId: first.svgFileId, previewFileId: first.previewFileId, sides };
        if (c.name) o.name = c.name; return o;
      }),
      files: files.map((f) => ({ id: f.id, kind: f.kind, filename: f.filename, contentType: f.contentType, size: f.blob.size }))
    };
    if (c0.handle) out.handle = c0.handle;
    if (c0.variant) out.variant = c0.variant;
    const own = job.isMain && view3d ? view3d.customSize() : null;   // the customer's own suitcase size (the cart line and the staff page show it)
    if (own) out.customSize = own;
    const mode = job.isMain ? editor.bulk.mode() : "single";   // a bulk order: the lines (one per size) and the total
    if (mode !== "single") {
      const lines = editor.bulk.lines();
      out.bulk = { mode, lines, total: totalOf(lines) };
      if (mode === "names") out.bulk.namesFileId = "names";
    }
    return out;
  }

  // ---------- the flow ----------
  // The designs to save, in order: the main one, then each gift. A gift is drawn without a screen (headless.js), only when its turn comes.
  // keepMain: the main design's job from before (its work so far is kept when only the gifts changed).
  function makeJobs(keepMain = null) {
    const main = keepMain || { isMain: true, label: "Your design", ctx, makeEditor: async () => editor, withPhotos: true, session: null };
    const giftJobs = extras.map((g, i) => ({
      gift: g, isMain: false, label: `Gift ${i + 1} of ${extras.length}`, withPhotos: false, session: null,
      ctx: { shop: ctx.shop, product: g.shape, handle: g.handle, variant: g.variantId || "" },
      makeEditor: async () => {
        const { createHeadless, ensureSidePhotos } = await import("./headless.js");
        await ensureSidePhotos(g.sides, g.design);
        return createHeadless({ product: g.product, sides: g.sides, design: g.design, sources: g.sources, originals: g.originals });
      }
    }));
    return [main, ...giftJobs];
  }

  // Saves one design. Work that is done stays in job.session, so "Try again" carries on where it stopped.
  async function runJob(job) {
    const s = job.session || (job.session = { built: null, files: null, design: null, sent: new Set(), done: false });
    if (s.done) return s;
    const say = (text, doneCount, total) => showProgress(jobs.length > 1 ? `${job.label} · ${text}` : text, doneCount, total);
    if (!s.built) {
      say("Getting your files ready…");
      const ed = await job.makeEditor();
      try {
        s.built = await buildFiles(ed, (i, n) => say(`Getting your files ready… ${Math.min(i + 1, n)} of ${n}`, i, n));
      } finally { if (ed !== editor && ed.dispose) ed.dispose(); }
      s.files = plan(s.built, job.withPhotos);
      checkLimits(s.files);
    }
    if (!s.design) {
      say("Saving your design…");
      s.design = await api.createDesign(payload(job, s.built, s.files));
      s.sent.clear();
    }
    for (const f of s.files) {
      if (s.sent.has(f.id)) continue;
      say(`Uploading ${s.sent.size + 1} of ${s.files.length}…`, s.sent.size, s.files.length);
      const url = s.design.uploads.get(f.id);
      if (!url) throw new api.ApiError("The server did not give us a place for every file.", { status: 502, retryable: true });
      try { await api.putFile(url, f.blob, f.contentType); }
      catch (e) { if (e instanceof api.ApiError && !e.retryable) s.design = null; throw e; }   // a refused file: start with a fresh design next time
      s.sent.add(f.id);
    }
    say("Almost done…", s.files.length, s.files.length);
    try { await api.completeDesign(s.design.designId); }
    catch (e) { if (e instanceof api.ApiError && e.status === 409) s.sent.clear(); throw e; }   // some files did not arrive: send them all again
    s.done = true;
    return s;
  }
  async function run() {
    for (const job of jobs) await runJob(job);
    return jobs.map((j) => j.session.design.designId);
  }

  async function confirm() {
    if (submitting || blocked || finished) return;             // no double submits
    // The gifts ticked now. The main design and the gifts of a try that failed keep their work while the list is the same; a changed
    // list of gifts is made again (the main design keeps its work).
    const want = gifts ? gifts.selected() : [], had = jobs.slice(1).map((j) => j.gift);
    const same = want.length === had.length && want.every((g, i) => g.handle === had[i].handle && g.variantId === had[i].variantId && g.design === had[i].design);
    extras = want;
    if (!same) jobs = makeJobs(jobs[0]);
    submitting = true; btn.disabled = true; chk.disabled = true; closeBtn.disabled = true;
    if (gifts) gifts.lock(true);
    try {
      const ids = await run();
      finished = true;
      editor.clearAfterConfirm();
      if (previewMode) {
        const staff = new URL("staff.html", location.href); staff.search = "?id=" + encodeURIComponent(ids[0]);
        showDone(staff.href);
        submitting = false; closeBtn.disabled = false;
      } else if (ctx.embed) {
        showProgress("Done. Adding your design to your cart…");
        ctx.embed.done({ design: ids[0], variant: ctx.variant, extra: ids.slice(1) });
      } else {
        showProgress("Done. Taking you back to the shop…");
        location.assign(api.withReturnParams(ctx.returnUrl, ids[0], ctx.variant, ids.slice(1)));
      }
    } catch (e) {
      submitting = false; closeBtn.disabled = false; chk.disabled = false;
      btn.disabled = blocked || !chk.checked;
      if (gifts) gifts.lock(false);
      showError(e);
    }
  }
  btn.addEventListener("click", confirm);

  return {
    open, close,
    giftsChanged: () => { if (isOpen()) showOrder(); },
    // The suitcase size changed (view3d.js): the warnings, Confirm and the order follow.
    sizeChanged: () => { if (!isOpen() || submitting || finished) return; renderWarnings(); showOrder(); btn.disabled = blocked || submitting || !chk.checked; }
  };
}
